import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppSelector } from "@/redux/hooks";

import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";
import { selectTourState } from "@/pages/Private/redux/onboardingTour/onboardingTour.slice";
import { TourStepId } from "@/pages/Private/redux/onboardingTour/schema";

import { DashboardFirstSteps } from "../schema/dashboard";
import { firstStepsToItems, FirstStepItem } from "../utils/firstStepsToItems";
import { filterOnboardingItems } from "../utils/filterOnboardingItems";

const NO_COMPLETED_STEPS: TourStepId[] = [];

/**
 * The onboarding checklist items the current user can actually see, after
 * permission gating. Merges the dashboard first-steps flags with the
 * setup-guide tour state (completed steps + generation readiness). Single
 * source of truth so the briefing-strip progress count and the checklist
 * always agree.
 */
export const useOnboardingItems = (firstSteps: DashboardFirstSteps): FirstStepItem[] => {
	const { t } = useTranslation();
	const { user } = useAppSelector(authSelector);
	const tourState = useAppSelector(selectTourState);
	const isSequencerActive = useIsFeatureActive(FeaturesCodes.SEQUENCER);
	const linkedinAllowed = Boolean(user?.linkedinAllowed);

	const completedSteps = tourState?.completedSteps ?? NO_COMPLETED_STEPS;
	const readiness = tourState?.readiness ?? null;

	return useMemo(
		() =>
			filterOnboardingItems(firstStepsToItems(firstSteps, { completedSteps, readiness, t }), {
				linkedinAllowed,
				isSequencerActive: Boolean(isSequencerActive),
			}),
		[firstSteps, completedSteps, readiness, t, linkedinAllowed, isSequencerActive]
	);
};
